import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

interface ClickableCardModalProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  metrics?: {
    total: number;
    change?: number;
    changeType?: 'increase' | 'decrease' | 'neutral';
    unit?: string;
  };
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'indigo';
  titleColor?: string;
  description?: string;
}

const getColorValue = (color: string): string => {
  const colorMap: Record<string, string> = {
    blue: '#3b82f6',
    green: '#10b981',
    purple: '#a855f7',
    orange: '#f97316',
    red: '#ef4444',
    indigo: '#6366f1',
    emerald: '#10b981',
    cyan: '#06b6d4',
    pink: '#ec4899'
  };
  return colorMap[color] || color;
};

const ClickableCardModal: React.FC<ClickableCardModalProps> = ({
  title,
  icon: Icon,
  onClick,
  metrics,
  titleColor,
  description
}) => {
  const formatNumber = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  };

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      onClick={onClick}
      className="w-full border-2 rounded-2xl shadow-lg overflow-hidden text-left transition-all duration-300 group"
      style={{
        backgroundColor: 'var(--bg-card)',
        borderColor: 'var(--border-primary)',
        boxShadow: 'var(--shadow-lg)'
      }}
      whileHover={{
        scale: 1.02,
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
      }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Header */}
      <div className="px-6 py-5">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <motion.div
              className="p-3 rounded-xl shadow-sm group-hover:scale-110 transition-transform duration-200"
              style={{
                backgroundColor: 'var(--bg-tertiary)',
                opacity: 0.8
              }}
              whileHover={{ scale: 1.1 }}
            >
              <div style={{ color: titleColor ? getColorValue(titleColor) : 'var(--primary)' }}>
                <Icon className="h-6 w-6" />
              </div>
            </motion.div>
            <div className="flex-1">
              <h2
                className="text-xl font-bold bg-gradient-to-r bg-clip-text text-transparent"
                style={{
                  backgroundImage: titleColor
                    ? `linear-gradient(135deg, ${getColorValue(titleColor)} 0%, ${getColorValue(titleColor)}dd 100%)`
                    : undefined,
                  color: titleColor ? undefined : 'var(--text-primary)'
                }}
              >
                {title}
              </h2>
              {description && (
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {description}
                </p>
              )}
            </div>
          </div>

          <motion.div
            className="p-2 rounded-lg"
            style={{
              backgroundColor: titleColor ? `${getColorValue(titleColor)}20` : 'var(--bg-tertiary)',
              color: titleColor ? getColorValue(titleColor) : 'var(--text-secondary)'
            }}
            whileHover={{ x: 5 }}
          >
            <ArrowRight className="h-5 w-5" />
          </motion.div>
        </div>

        {/* Metrics */}
        {metrics && (
          <div className="flex items-center space-x-4">
            <div>
              <p
                className="text-xs mb-1"
                style={{ color: 'var(--text-secondary)' }}
              >
                Total
              </p>
              <p
                className="text-2xl font-bold"
                style={{
                  color: titleColor ? getColorValue(titleColor) : 'var(--text-primary)'
                }}
              >
                {formatNumber(metrics.total)}
              </p>
            </div>
            {metrics.unit && (
              <p
                className="text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                {metrics.unit}
              </p>
            )}
            {metrics.change !== undefined && (
              <div
                className="ml-auto px-3 py-1 rounded-lg text-sm font-medium"
                style={{
                  backgroundColor: metrics.change > 0 ? '#10b98120' : '#ef444420',
                  color: metrics.change > 0 ? '#10b981' : '#ef4444'
                }}
              >
                {metrics.change > 0 ? '+' : ''}{metrics.change}%
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom accent line */}
      <motion.div
        className="h-1 w-full"
        style={{
          backgroundColor: titleColor ? getColorValue(titleColor) : 'var(--primary)',
          transformOrigin: 'left'
        }}
        initial={{ scaleX: 0 }}
        whileHover={{ scaleX: 1 }}
        transition={{ duration: 0.3 }}
      />
    </motion.button>
  );
};

export default ClickableCardModal;
